<template>
  <BListGroup>
    <BListGroupItem
      v-for="(item, index) in orderedItems()"
      :key="index"
      class="flex-column align-items-start pl-1 pr-1 pt-1 pb-2"
      :class="
        index == 0
          ? 'rounded-top'
          : index !== itemsCount() - 1
          ? 'rounded-bottom'
          : ''
      "
    >
      <div :class="`item-head ${bootstrapVariant(item.variant)}`" />
      <div v-if="index !== 0" class="item-upper-tail" />
      <div v-if="index !== itemsCount() - 1" class="item-bottom-tail" />

      <BSpinner
        v-if="item.spinner"
        :variant="bootstrapVariant()"
        class="ml-4"
      />

      <div v-if="!item.spinner" class="item-content">
        <div class="d-flex w-100 justify-content-between">
          <h5 class="mb-1 w-75">
            {{ item.title }}
          </h5>
          <small :id="timestampElementId(item)" class="mt-md-2">
            {{
              humanFriendlyTime
                ? formatAgo(item.timestamp)
                : formatFull(item.timestamp)
            }}</small
          >

          <BTooltip :target="timestampElementId(item)" triggers="hover">
            {{
              humanFriendlyTime
                ? formatFull(item.timestamp)
                : formatAgo(item.timestamp)
            }}
          </BTooltip>
        </div>

        <small class="mb-1 item-description">
          <slot :item="item" name="item">
            {{ item.content || "" }}
          </slot>
        </small>
      </div>
    </BListGroupItem>
  </BListGroup>
</template>

<script>
export default {
  name: "BootstrapVueTimeline",
  props: {
    /**
     * A list of timeline items to display. Supported keys include: `timestamp`, `title`, `content`.
     */
    items: Array,
    /**
     * The component displays a vertical timeline in the order of the `items` prop.
     * If `reserve` is set to false, it displays items in reverse order.
     *
     *  Default: false.
     */
    reverse: Boolean,
    /**
     * If true, display a loading spinner in the last item.
     */
    loading: Boolean,
    /**
     * Controls the date format in the tooltip when you hover the human friendly time.
     *  Default: 'yyyy-MM-dd HH:mm:ss'
     */
    dateFormat: String,
    /**
     * Color variant. It supports [Bootstrap color variants](https://bootstrap-vue.org/docs/reference/color-variants#color-variants-and-css-class-mapping), including 'primary', 'success'.
     *  Default: 'primary'
     */
    variant: String,
    /**
     * Displays human friendly time, e.g., '2 months ago'. If false, display the time as formatted according to the `dateFormat` param.
     *  Default: true
     */
    humanFriendlyTime: {
      type: Boolean,
      default: true,
    },
  },
  methods: {
    bootstrapVariant(itemVariant) {
      return `bg-${itemVariant} text-${itemVariant} border-${itemVariant}`;
    },
    orderedItems() {
      let items = this.items;
      if (this.loading) {
        items = [
          ...items,
          { spinner: true, timestamp: "time", title: "loading" },
        ];
      }

      if (this.reverse) {
        items.reverse();
      }

      return items;
    },
    itemsCount() {
      if (this.loading) {
        return this.items.length + 1;
      }

      return this.items.length;
    },
    formatAgo(timestamp) {
      return Helper.timeAgo(timestamp);
    },
    formatFull(timestamp) {
      return `${this.customFullDateConvert(
        timestamp
      )} - ${this.customConvertHourAndMinute(timestamp)}`;
    },
    timestampElementId(item) {
      return item.timestamp + item.title + "-timestamp";
    },
  },
  mixins: [timeConvertUtils],
  components: {
    BListGroup,
    BListGroupItem,
    BSpinner,
    BTooltip,
  },
};
import Helper from "@/libs/Helper";
import timeConvertUtils from "@/mixins/timeConvertUtils";
import { BListGroup, BListGroupItem, BSpinner, BTooltip } from "bootstrap-vue";
</script>

<style scoped>
.list-group-item {
  position: relative;
  border: none;
  margin: 0;
  padding: 0 0 20px;
  box-sizing: border-box;
}

.item-head {
  position: absolute;
  width: 10px;
  height: 10px;
  border: 2px solid transparent;
  border-radius: 100px;
}
.item-upper-tail {
  position: absolute;
  top: 0px;
  left: 18px;
  height: 14px;
  border-left: 2px solid #e8e8e8;
}

.item-bottom-tail {
  position: absolute;
  top: 24px;
  left: 18px;
  height: calc(100% - 10px);
  border-left: 2px solid #e8e8e8;
}

.item-content {
  position: relative;
  top: -6px;
  margin: 0 0 0 18px;
  word-break: break-word;
}

.item-description {
  display: block;
  text-align: left;
}

.light {
  background-color: #f8f9fa;
  color: #f8f9fa;
  border-color: #f8f9fa;
}

.dark {
  background-color: #343a40;
  color: #343a40;
  border-color: #343a40;
}
</style>
